import React from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { useAuth } from './hooks/useAuth';
import theme from './styles/theme';
import GlobalStyles from './styles/globalStyles';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import EmployeeLayout from './layouts/EmployeeLayout';
import CustomerLayout from './layouts/CustomerLayout';
import SupportLayout from './layouts/SupportLayout';
import AuthLayout from './layouts/AuthLayout';

// Support
import SupportConsole from './pages/support/SupportConsole';
import SupportMirror from './pages/support/SupportMirror';
import SupportMirrorCapture from './components/support/SupportMirrorCapture';
import ImpersonationBanner from './components/common/ImpersonationBanner';

// Auth pages
import Login from './pages/auth/Login';
import AdminLogin from './pages/auth/AdminLogin';
import LinkLogin from './pages/auth/LinkLogin';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import CreatePassword from './pages/auth/CreatePassword';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import Users from './pages/admin/Users';
import Customers from './pages/admin/Customers';
import Employees from './pages/admin/Employees';
import Assignments from './pages/admin/Assignments';
import TimeReports from './pages/admin/TimeReports';
import Invoicing from './pages/admin/Invoicing';
import Payroll from './pages/admin/Payroll';
import Integrations from './pages/admin/Integrations';
import Settings from './pages/admin/Settings';
import Configuration from './pages/admin/Configuration';
import SystemSettings from './pages/admin/SystemSettings';
import EmailDelivery from './pages/admin/EmailDelivery';
import SuperadminRoute from './components/common/SuperadminRoute';
import AdminOnlyRoute from './components/common/AdminOnlyRoute';

// Employee pages
import EmployeeDashboard from './pages/employee/Dashboard';
import EmployeeTimeEntries from './pages/employee/TimeEntries';
import EmployeeTimeEntryDetail from './pages/employee/TimeEntryDetail';
import EmployeeTimeEntryForm from './pages/employee/TimeEntryForm';
import CalendarView from './pages/employee/CalendarView';
import WeeklyEntry from './pages/employee/WeeklyEntry';
import Profile from './pages/employee/Profile';
import NotificationPreferences from './pages/employee/NotificationPreferences';
import LegalDocuments from './pages/legal/LegalDocuments';

// Customer pages
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerApprovals from './pages/customer/Approvals';
import CustomerApprovalDetail from './pages/customer/ApprovalDetail';
import CustomerEmployees from './pages/customer/Employees';
import CustomerReports from './pages/customer/Reports';
import CustomerCompany from './pages/customer/Company';

// Protected route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, isAuthenticated, loading, sessionError, sessionChecking } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>; // You could use a proper loading spinner here
  }

  if (!isAuthenticated) {
    if (sessionError || sessionChecking) return null; // App renders session Retry.
    // Redirect to login if not authenticated
    const loginPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/support') ? '/adminlogin' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Handle first-time login redirection
  const requiresPassword = ['admin', 'support'].includes(currentUser.userType) && currentUser.isFirstLogin === true;
  if (requiresPassword && location.pathname !== '/create-password') {
    return <Navigate to="/create-password" replace />;
  }

  // If on create-password page but not in first-time login state, redirect to dashboard
  if (location.pathname === '/create-password' && !requiresPassword) {
    return <Navigate to={getRedirectPath(currentUser.userType)} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(currentUser.userType)) {
    // Redirect to appropriate dashboard if role doesn't match
    return <Navigate to={getRedirectPath(currentUser.userType)} replace />;
  }

  return children;
};

// Helper function to get redirect path based on user type
const getRedirectPath = (userType) => {
  switch (userType) {
    case 'admin':
      return '/admin/dashboard';
    case 'employee':
      return '/employee/calendar';
    case 'customer':
      return '/customer/dashboard';
    case 'support':
      return '/support';
    default:
      return '/login';
  }
};

const App = () => {
  const { isAuthenticated, currentUser, loading, sessionError, sessionChecking, retrySession } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // Determine the redirect path based on user type
  const getAppRedirectPath = () => {
    if (!currentUser) return '/login';
    
    // Check for first-time login
    if (['admin', 'support'].includes(currentUser.userType) && currentUser.isFirstLogin === true) {
      return '/create-password';
    }
    
    return getRedirectPath(currentUser.userType);
  };

  // Show loading screen while authentication state is being determined
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px'
        }}>
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <ImpersonationBanner />
      <SupportMirrorCapture />
      {!isAuthenticated && (sessionError || sessionChecking) && <section role="status" style={{ maxWidth: 600, padding: 24, margin: '24px auto' }}>
        <p>{t('auth.access.sessionRetry')}</p>
        <button type="button" className="submit-button" onClick={retrySession} disabled={sessionChecking}>
          {t(sessionChecking ? 'auth.linkLogin.wait' : 'common.retry')}
        </button>
        <Link to="/login" className="forgot-password-link">{t('auth.access.send')}</Link>
      </section>}
      <Routes>
        {/* Auth Routes */}
        <Route path="/" element={<AuthLayout />}>
          <Route index element={isAuthenticated ? <Navigate to={getAppRedirectPath()} /> : <Login />} />
          {/* Explicit sign-in/recovery stays available when a different account
              is already open in this browser. Only the homepage auto-continues. */}
          <Route path="login" element={<Login />} />
          {/* Identifier-first admin/support sign-in (password + Windows Hello) */}
          <Route path="adminlogin" element={<AdminLogin />} />
          {/* Welcome-mail login link. Deliberately NOT gated on isAuthenticated:
              the page must always exchange the ?token= and refresh the session,
              even when someone is already signed in. */}
          <Route path="link-login" element={<LinkLogin key={`${location.search}${location.hash}`} />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="create-password" element={
            <ProtectedRoute>
              <CreatePassword />
            </ProtectedRoute>
          } />
        </Route>

        {/* Legal documents. Deliberately outside every guard and outside
            AuthLayout (whose narrow AuthBox is wrong for long-form prose):
            the privacy policy and the licence terms must be readable BEFORE
            signing in — GDPR art. 12-13 transparency, and standard terms bind
            only if they were available before the agreement was concluded.
            The page still shows the restricted documents to a signed-in
            reader, because apiClient sends the bearer token and the API widens
            the catalogue accordingly. A first-login user can reach it too,
            which ProtectedRoute would otherwise prevent. */}
        <Route path="/legal" element={<LegalDocuments />} />
        <Route path="/legal/:slug" element={<LegalDocuments />} />

        {/* Admin Routes.
            Support shares this tree for the three sections it owns — Customers,
            Employees and Assignments — so those pages stay one implementation
            rather than two. Everything else sits behind AdminOnlyRoute, which
            closes new pages by default. */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'support']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={(
              <Navigate
                to={currentUser?.userType === 'support' ? '/admin/customers' : '/admin/dashboard'}
                replace
              />
            )}
          />

          {/* Admin + support */}
          <Route path="customers" element={<Customers />} />
          <Route path="employees" element={<Employees />} />
          <Route path="assignments" element={<Assignments />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
          {/* E-mail delivery is shared too: support sends invitations and login
              links and must be able to see whether they went out. The API
              limits a support user to the rows they requested themselves. */}
          <Route path="email" element={<EmailDelivery />} />

          {/* Admin only */}
          <Route element={<AdminOnlyRoute />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="time-reports" element={<TimeReports />} />
            <Route path="invoicing" element={<Invoicing />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="configuration"
              element={(
                <SuperadminRoute>
                  <Configuration />
                </SuperadminRoute>
              )}
            />
            <Route path="system" element={<SystemSettings />} />
            {/* Add other admin routes as needed */}
          </Route>
        </Route>

        {/* Employee Routes */}
        <Route
          path="/employee"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/employee/calendar" replace />} />
          <Route path="week" element={<WeeklyEntry />} />
          <Route path="dashboard" element={<EmployeeDashboard />} />
          <Route path="time-entries" element={<EmployeeTimeEntries />} />
          <Route path="time-entries/new" element={<EmployeeTimeEntryForm />} />
          <Route path="time-entries/:id" element={<EmployeeTimeEntryDetail />} />
          <Route path="calendar" element={<CalendarView />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
        </Route>

        {/* Customer Routes */}
        <Route
          path="/customer"
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/customer/dashboard" replace />} />
          <Route path="dashboard" element={<CustomerDashboard />} />
          <Route path="approvals" element={<CustomerApprovals />} />
          <Route path="approvals/:id" element={<CustomerApprovalDetail />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
          <Route path="employees" element={<CustomerEmployees />} />
          <Route path="reports" element={<CustomerReports />} />
          <Route path="company" element={<CustomerCompany />} />
        </Route>

        {/* Support Routes (impersonation console) */}
        <Route
          path="/support"
          element={
            <ProtectedRoute allowedRoles={['support']}>
              <SupportLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SupportConsole />} />
          <Route path="mirror/:id" element={<SupportMirror />} />
        </Route>

        {/* Catch All Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
